// 差分ベースの履歴。
//
// - Document (JSON、ピクセルを含まないので小さい) は変更前後の参照を持つ。
//   mutate() が毎回コピーを作るので、before/after は不変として扱える。
// - ピクセルはタイルパッチ (旧タイルへの参照) だけを持つ。コピーはしない。
// - 予算 (バイト数) と件数の上限を超えたら最古から捨てる。
// - 参照プロジェクトの mergeKey + 時間窓は不採用。ドラッグ等は
//   documentStore のトランザクションで 1 エントリにまとめる。

import type { Document } from '@/types/document'
import type { TilePatch } from '@/pixels/pixelSurface'
import type { SurfacePatch } from '@/pixels/pixelStore'

export interface HistoryLabel {
  key: string
  params?: Record<string, string | number>
}

export interface HistoryEntry {
  id: string
  label: HistoryLabel
  at: number
  before: Document
  after: Document
  tilePatches: TilePatch[]
  surfacePatches: SurfacePatch[]
  /** このエントリが保持しているタイルの概算バイト数 */
  bytes: number
}

export interface HistoryLimits {
  maxEntries: number
  maxBytes: number
}

export const DEFAULT_HISTORY_LIMITS: HistoryLimits = {
  maxEntries: 50,
  maxBytes: 512 * 1024 * 1024
}

export function estimateEntryBytes(tilePatches: TilePatch[], surfacePatches: SurfacePatch[]): number {
  let n = 0
  for (const p of tilePatches) {
    if (p.before) n += p.before.data.byteLength
    if (p.after) n += p.after.data.byteLength
  }
  for (const p of surfacePatches) {
    if (p.before) n += p.before.byteLength()
  }
  return n
}

export class HistoryManager {
  private undoStack: HistoryEntry[] = []
  private redoStack: HistoryEntry[] = []
  private limits: HistoryLimits
  /** 変更のたびに +1。Vue 側はこれを購読して canUndo 等を再計算する */
  version = 0
  private seq = 0

  constructor(limits: Partial<HistoryLimits> = {}) {
    this.limits = { ...DEFAULT_HISTORY_LIMITS, ...limits }
  }

  setLimits(limits: Partial<HistoryLimits>): void {
    this.limits = { ...this.limits, ...limits }
    this.trim()
    this.version++
  }

  push(entry: Omit<HistoryEntry, 'id' | 'at'>): HistoryEntry {
    const full: HistoryEntry = { ...entry, id: `h${++this.seq}`, at: Date.now() }
    this.undoStack.push(full)
    this.redoStack = []
    this.trim()
    this.version++
    return full
  }

  private trim(): void {
    while (this.undoStack.length > this.limits.maxEntries) this.undoStack.shift()
    let bytes = this.totalBytes()
    while (this.undoStack.length > 1 && bytes > this.limits.maxBytes) {
      const dropped = this.undoStack.shift()!
      bytes -= dropped.bytes
    }
  }

  totalBytes(): number {
    let n = 0
    for (const e of this.undoStack) n += e.bytes
    for (const e of this.redoStack) n += e.bytes
    return n
  }

  get canUndo(): boolean {
    return this.undoStack.length > 0
  }

  get canRedo(): boolean {
    return this.redoStack.length > 0
  }

  get undoCount(): number {
    return this.undoStack.length
  }

  get redoCount(): number {
    return this.redoStack.length
  }

  /** undo 対象を取り出して redo 側へ移す。呼び出し側がパッチを逆適用する */
  popUndo(): HistoryEntry | undefined {
    const e = this.undoStack.pop()
    if (!e) return undefined
    this.redoStack.push(e)
    this.version++
    return e
  }

  popRedo(): HistoryEntry | undefined {
    const e = this.redoStack.pop()
    if (!e) return undefined
    this.undoStack.push(e)
    this.version++
    return e
  }

  clear(): void {
    this.undoStack = []
    this.redoStack = []
    this.version++
  }

  /** 履歴パネル表示用 */
  entries(): { undo: readonly HistoryEntry[]; redo: readonly HistoryEntry[] } {
    return { undo: this.undoStack, redo: this.redoStack }
  }

  /** 履歴のどこかから参照されている pixelId (破棄してはいけない表面) */
  retainedPixelIds(): Set<string> {
    const ids = new Set<string>()
    const collect = (e: HistoryEntry) => {
      for (const p of e.surfacePatches) ids.add(p.pixelId)
      for (const p of e.tilePatches) ids.add(p.pixelId)
      for (const d of [e.before, e.after]) {
        for (const l of Object.values(d.layers)) {
          if (l.kind === 'raster') ids.add(l.pixelId)
          if (l.kind === 'smart') ids.add(l.sourceId)
          if (l.kind === 'adjustment' && l.adjustment.kind === 'color-lookup' && l.adjustment.lutPixelId) ids.add(l.adjustment.lutPixelId)
          if (l.mask) ids.add(l.mask.pixelId)
        }
        if (d.selection.pixelId) ids.add(d.selection.pixelId)
        for (const c of d.channels ?? []) ids.add(c.pixelId)
        for (const p of d.patterns ?? []) ids.add(p.pixelId)
      }
    }
    this.undoStack.forEach(collect)
    this.redoStack.forEach(collect)
    return ids
  }
}
